import { useMemo, useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Formik } from 'formik';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import moment from 'moment';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import EventForm, { eventSchema } from '../../components/events/form';
import { createEventMutation } from '../../apollo/events';
import { primaryCityQuery } from '../../apollo/cities';
import { copyEventQuery } from '../../apollo/events';
import Loading from '../../components/loading';
import ErrorMessage from '../../components/error-message';
import LoginRequiredNotice from '../../components/login-required-notice';
import MaterialLink from '../../components/material-link';
import { buildTitle } from '../../utils/layout';
import { processMutationError } from '../../utils/formik';
import config from '../../config';
import Main from '../../components/main';
import { momentParse, combineDateAndTime } from '../../utils/time';
import { useAppContext } from '../../contexts/app';
import MaterialLinkButton from '../../components/material-link-button';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    margin: theme.spacing(3, 0),
  },
  title: {
    margin: 0,
  },
  groupRideGuide: {
    padding: theme.spacing(2),
    border: '2px solid #ddd',
    margin: theme.spacing(2, 0, 3, 0),
  },
  loginRequiredNotice: {
    margin: theme.spacing(2, 0),
  },
}));

const NewEventPage = () => {
  const router = useRouter();
  const { query } = router;
  const { isLoggedIn } = useAppContext();
  const classes = useStyles();

  const {
    data: copyEventData,
    loading: copyEventLoading,
    error: copyEventError,
  } = useQuery(copyEventQuery, {
    variables: {
      id: query.copyEventId,
    },
    fetchPolicy: 'network-only',
    skip: !query.copyEventId,
  });
  const copyEvent = _get(copyEventData, 'event');

  const {
    data: primaryCityData,
    loading: primaryCityLoading,
    error: primaryCityError,
  } = useQuery(primaryCityQuery);
  const primaryCity = _get(primaryCityData, 'me.primaryCity');

  const [createEvent] = useMutation(createEventMutation);

  const initialValues = useMemo(() => {
    if (copyEvent) {
      return {
        ..._pick(copyEvent, [
          'hostType',
          'eventType',
          'registrationType',
          'name',
          'description',
          'startAtDetails',
          'startLocation',
          'startLocationAddress',
          'startLocationDetails',
          'loop',
          'pace',
          'distance',
          'regroup',
          'terrain',
          'weatherCancellation',
          'ageRestriction',
          'audienceRestriction',
          'endLocation',
          'endLocationAddress',
          'endLocationDetails',
          'infoUrl',
          'routeUrl',
          'categoryIds',
          'visibility',
          'approvalPrompt',
          'approvalDescription',
          'contactName',
          'contactPhoneNumber',
          'contactEmail',
        ]),
        startDate: moment(copyEvent.startAt),
        startTime: moment(copyEvent.startAt),
        endDate: copyEvent.endAt ? moment(copyEvent.endAt) : null,
        endTime: copyEvent.endAt ? moment(copyEvent.endAt) : null,
        currentImage: copyEvent.image,
        status: 'PUBLISHED',
        groupId: _get(copyEvent, 'group.id'),
      };
    }

    const startAt = momentParse().hour(19).minutes(0);
    return {
      eventType: 'RIDE',
      status: 'PUBLISHED',
      hostType: 'User',
      groupId: query.groupId || null,
      visibility: query.groupId ? 'GROUP' : 'PUBLIC', // TODO: set this based on group public/private
      startDate: startAt,
      startTime: startAt,
      categoryIds: [],
      ageRestriction: 'GENERAL',
      registrationType: 'OPEN',
    };
  }, [query, copyEvent]);

  const handleSubmit = useCallback((values, actions) => {
    createEvent({
      variables: {
        data: {
          ..._pick(values, [
            'hostType',
            'groupId',
            'eventType',
            'registrationType',
            'name',
            'description',
            'startAtDetails',
            'startLocation',
            'startLocationAddress',
            'startLocationDetails',
            'loop',
            'endLocation',
            'endLocationAddress',
            'endLocationDetails',
            'infoUrl',
            'routeUrl',
            'categoryIds',
            'visibility',
            'pace',
            'distance',
            'regroup',
            'terrain',
            'weatherCancellation',
            'ageRestriction',
            'audienceRestriction',
            'approvalPrompt',
            'approvalDescription',
            'contactName',
            'contactPhoneNumber',
            'contactEmail',
            'image', // not copied
            'status', // not copied
          ]),
          startAt: combineDateAndTime(values.startDate, values.startTime),
          endAt: combineDateAndTime(values.endDate, values.endTime),
          copyImageEventId: copyEvent && values.currentImage ? copyEvent.id : null,
        },
        citySlug: primaryCity.slug,
      },
    }).then(({ data: { createEvent: event } }) => {
      window.location = `${config().universal.baseUri}/events/${event.id}`;
    }).catch((error) => {
      processMutationError(error, actions);
    });
  }, [primaryCity, copyEvent]);

  if (primaryCityLoading || copyEventLoading) {
    return <Loading />;
  }
  if (primaryCityError || copyEventError) {
    return (
      <ErrorMessage
        message={(primaryCityError || copyEventError).message}
        error={(primaryCityError || copyEventError)}
      />
    );
  }

  return (
    <Main>
      <Head>
        <title>{buildTitle('Add Event')}</title>
        <link rel="canonical" href={`${config().universal.baseUri}/events/new`} />
      </Head>

      <div className={classes.header}>
        <Typography variant="h1" className={classes.title}>Add Event</Typography>
        <div>
          <MaterialLinkButton
            href="/event-series"
            variant="outlined"
          >
            Manage Event Series
          </MaterialLinkButton>
        </div>
      </div>

      {!isLoggedIn && (
        <LoginRequiredNotice
          returnTo="/events/new"
          action="create an event"
          className={classes.loginRequiredNotice}
        />
      )}

      <Paper className={classes.groupRideGuide}>
        See <MaterialLink href="/about" target="_blank">About Everyday Rides</MaterialLink> for info about the calendar and using the site. See the <MaterialLink href="/group-ride-guide" target="_blank">Group Ride Guide</MaterialLink> for tips on planning rides.
      </Paper>

      <Formik
        initialValues={initialValues}
        validationSchema={eventSchema}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <EventForm
            {...props}
            disabled={!isLoggedIn}
            name="new-event"
          />
        )}
      </Formik>
    </Main>
  );
};

export default NewEventPage;
